<template>
  <div class="f-h-below-title configurable-pages-list" :class="elClasses">
    <!-- eslint-disable-next-line max-len -->
    <div class="configurable-pages-list__search v-centerer generic-border generic-border--thin-border generic-border--focus-shadow">
      <Icon>{{ searchMedium }}</Icon>
      <input v-model="search" :placeholder="$t('message.FILTER_CFG_PAGES')">
    </div>

    <!-- Create a new bare page -->
    <button
      v-if="!selection"
      class="generic-border generic-border--text-btn generic-border--blue-border configurable-pages-list__add"
      @click="createPage()"
    >
      <div class="generic-border__left">
        <Icon class="generic-border__left-blue-icon">{{ addExtraBold }}</Icon>
        <span>{{ $t('message.CUST_PAGES_NEW') }}</span>
      </div>
      <Icon class="generic-border__right-arrow">{{ arrowDownLine }}</Icon>
    </button>

    <!-- Import a page from the config -->
    <button
      v-if="currentTeam && currentTeam.id && !selection"
      class="generic-border generic-border--text-btn generic-border--blue-border configurable-pages-list__add"
      @click="importPage()"
    >
      <div class="generic-border__left">
        <Icon class="generic-border__left-blue-icon">{{ cfgPageMedium }}</Icon>
        <span>{{ $t('message.CFG_PAGES_IMPORT_TEMPLATE') }}</span>
      </div>
      <Icon class="generic-border__right-arrow">{{ arrowDownLine }}</Icon>
    </button>

    <div v-if="!selection" class="configurable-pages-list__sep" />

    <div class="configurable-pages-list__content">
      <div
        v-for="page in filteredPages"
        :key="page.id"
        class="configurable-pages-list__item generic-border generic-border--clickable generic-border--thin-border"
        :class="{ 'generic-border--focused': selectedPages.includes(page.id) }"
         @click="onPageClick(page)"
      >
        <div class="v-centerer configurable-pages-list__item__text">
          <span class="name">{{ page.name }}</span>
          <span class="info ml5">{{ $tc('message.CUST_PAGES_N_WIDGET', page.nWidgets, { n: page.nWidgets }) }}</span>
        </div>

        <div v-if="!selection" class="configurable-pages-list__item__delete h-v-centerer">
          <div
            class="generic-border generic-border--icon-button generic-border--clickable generic-border--gray-border"
            @click.stop="deletePage(page)"
          >
            <Icon>{{ deleteMedium }}</Icon>
          </div>
        </div>
      </div>
    </div>
    <button
      v-if="selection"
      class="configurable-pages-list__ok generic-border generic-border--text-btn generic-border--blue"
      @click="$emit('ok', selectedPages)"
    >
      {{ $t('message.OK') }}
    </button>

    <Modal v-model="importingPage">
      <ConfigurablePagesList templates selection @ok="createPage" />
    </Modal>
  </div>
</template>

<script>
/**
 * Fetch and display a filterable list of configurable pages.
 * Also allows creating new pages or deleting some.
 *
 * @vue-prop {Boolean} selection - Allow selecting a page, instead of propagating the click event
 *                                 Also add an 'OK' button to emit the ids of selected pages.
 * @vue-prop {Boolean} templates - Display a list of public templates
 *
 * @vue-event {Array} ok - Emit an array of selected page ids
 */

import { uniq } from 'lodash';
// Components
import Icon from '@/components/icon.vue';
import Modal from '@/components/modal.vue';
// Utils
import {
  addExtraBold,
  arrowDownLine,
  cfgPageMedium,
  deleteMedium,
  searchMedium,
} from '@/assets/icons/';

export default {

  name: 'ConfigurablePagesList',

  components: {
    Icon,
    Modal,
  },

  props: {
    selection: Boolean,
    templates: Boolean,
  },

  data() {
    return {
      addExtraBold,
      arrowDownLine,
      cfgPageMedium,
      deleteMedium,
      searchMedium,
      search: '',
      pages: [],
      // When selection is true, allow setting a page as selected
      selectedPages: [],
      importingPage: false,
    };
  },

  computed: {
    /** Object: Cllasses applied to $el */
    elClasses() {
      return {
        'configurable-pages-list--selection': this.selection,
      };
    },
    /** Array: Array of pages mathcing the search terms */
    filteredPages() {
      const effectiveSearch = this.search.toUpperCase().split(/[\s,-.\/\']/);
      return this.pages.filter(page => !effectiveSearch.some(term => !page.name.toUpperCase().includes(term)));
    },
  },

  created() {
    this.refresh();
  },

  methods: {
    refresh() {
      this.$http.post('/server/customPages/get.php', { templates: this.templates }).then((response) => {
        if (response && response.data instanceof Array) {
          this.pages = response.data;
        } else {
          toastr.error(this.$t('message.CONNECTION_ERROR'));
        }
      });
    },
    /** Handle click on a page item */
    onPageClick(page) {
      if (this.selection) {
        if (this.selectedPages.includes(page.id)) {
          this.selectedPages = this.selectedPages.filter(pageId => pageId !== page.id);
        } else {
          this.selectedPages = uniq([...this.selectedPages, page.id]);
        }
      } else {
        this.$emit('pageClick', page);
      }
    },
    /** Create a new blank page */
    createPage(fromTemplateIds) {
      this.search = '';
      this.$http.post('/server/customPages/create.php', { fromTemplateIds }).then((response) => {
        if (response && response.data instanceof Array) {
          this.importingPage = false;
          toastr.success(this.$t('message.CFG_PAGES_CREATED'));
          this.pages = response.data;
        } else {
          toastr.error(this.$t('message.CONNECTION_ERROR'));
        }
      });
    },
    /** Allow importing a list of cfg pages templates */
    importPage() {
      this.importingPage = true;
    },
    /** Delete a page */
    deletePage(page) {
      calert(this.$t('message.CFG_PAGES_CONFIRM_DELETE'), [
        {
          title: this.$t('message.YES'),
          action: () => {
            this.confirmDeletePage(page);
          },
          sClass: 'red',
        },
        {
          title: this.$t('message.NO'),
        },
      ]);
    },
    confirmDeletePage(page) {
      this.search = '';
      this.$http.post('/server/customPages/delete.php', { id: page.id }).then((response) => {
        if (response && response.data instanceof Array) {
          toastr.success(this.$t('message.CFG_PAGES_DELETED'));
          this.pages = response.data;
        } else {
          toastr.error(this.$t('message.CONNECTION_ERROR'));
        }
      });
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";

.configurable-pages-list {
  height: 100%;
  padding: 1em;
  box-sizing: border-box;
  display: flex;
  flex-flow: column;

  &__search {
    flex: 0 0 auto;
    background: #fff;
    margin: 0.5em 0;
    border: 1px solid #888888;
    height: 40px;

    .icon {
      width: 0.9em;
      height: 0.9em;
    }

    input {
      margin: 0px;
      border: none;
      background: transparent;
      width: 100%;
      height: 36px;
      outline: none;
      padding-left: 0.9rem;
    }
  }

  &--selection {
    .configurable-pages-list__search {
      margin-right: 3rem;
    }
  }

  &__add {
    flex: 0 0 auto;
    margin-top: 0.5em;
    align-self: flex-start;
    width: 350px;
  }

  &__sep {
    width: calc(100% + calc(2 * #{$settings-item-overflow-padding}));
    margin: 2rem -#{$settings-item-overflow-padding};
    height: 1px;
    background: $color-gray-background-hover;
  }

  &__content {
    flex: 1;
  }

  &__ok {
    flex: 0 0 auto;
    align-self: center;
    margin-top: 1rem;
  }

  &__item {
    display: flex;
    align-items: center;
    margin-top: 0.7rem;
    height: $main-tile-height;

    &__text {
      flex: 1;
      min-width: 0px;
    }

    &__delete {
      flex: 0 0 auto;
    }
  }
}

@media (max-width: $smartphone-portrait) {
  .configurable-pages-list {
    &__add {
      align-self: unset;
      width: auto;
    }
  }
}
</style>
